import { memo } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/forms';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { BroadcastMedia, BroadcastButton } from '../types';
import { PARSE_MODE_OPTIONS, MEDIA_TYPES } from './constants';
import { MediaUploader } from './media-uploader';

// ─── Telegram media section ──────────────────────────────────────────────────

interface TelegramMediaSectionProps {
  mediaEnabled: boolean;
  setMediaEnabled: (value: boolean) => void;
  mediaType: BroadcastMedia['type'];
  setMediaType: (value: BroadcastMedia['type']) => void;
  mediaUrl: string;
  setMediaUrl: (value: string) => void;
  mediaCaption: string;
  setMediaCaption: (value: string) => void;
  mediaParseMode: 'Markdown' | 'HTML' | 'none';
  setMediaParseMode: (value: 'Markdown' | 'HTML' | 'none') => void;
  isPending: boolean;
}

export const TelegramMediaSection = memo(function TelegramMediaSection({
  mediaEnabled,
  setMediaEnabled,
  mediaType,
  setMediaType,
  mediaUrl,
  setMediaUrl,
  mediaCaption,
  setMediaCaption,
  mediaParseMode,
  setMediaParseMode,
  isPending,
}: TelegramMediaSectionProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Checkbox
          id="media-enabled"
          checked={mediaEnabled}
          onCheckedChange={(value) => setMediaEnabled(value === true)}
          disabled={isPending}
        />
        <label htmlFor="media-enabled" className="text-sm">
          Добавить медиа-вложение
        </label>
      </div>

      {mediaEnabled && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="Тип медиа" name="mediaType" required>
              <Select
                value={mediaType}
                onValueChange={(value) => setMediaType(value as BroadcastMedia['type'])}
                disabled={isPending}
              >
                <SelectTrigger><SelectValue placeholder="Тип" /></SelectTrigger>
                <SelectContent>
                  {MEDIA_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>

            <FormField label="Разметка подписи" name="mediaParseMode">
              <Select
                value={mediaParseMode}
                onValueChange={(value) => setMediaParseMode(value as 'Markdown' | 'HTML' | 'none')}
                disabled={isPending}
              >
                <SelectTrigger><SelectValue placeholder="Без разметки" /></SelectTrigger>
                <SelectContent>
                  {PARSE_MODE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </div>

          <MediaUploader
            mediaType={mediaType}
            value={mediaUrl}
            onChange={setMediaUrl}
            disabled={isPending}
          />

          <FormField label="Подпись" name="mediaCaption">
            <Input
              value={mediaCaption}
              onChange={(e) => setMediaCaption(e.target.value)}
              placeholder="Подпись к медиа"
              disabled={isPending}
            />
          </FormField>
        </div>
      )}
    </div>
  );
});

// ─── Buttons section ─────────────────────────────────────────────────────────

interface ButtonsSectionProps {
  title: string;
  hint: string;
  buttons: BroadcastButton[];
  onAdd: () => void;
  onUpdate: (index: number, next: Partial<BroadcastButton>) => void;
  onRemove: (index: number) => void;
  isPending: boolean;
  showCallbackData?: boolean;
}

export const ButtonsSection = memo(function ButtonsSection({
  title,
  hint,
  buttons,
  onAdd,
  onUpdate,
  onRemove,
  isPending,
  showCallbackData = false,
}: ButtonsSectionProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-medium">{title}</h4>
          <p className="text-xs text-muted-foreground">{hint}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onAdd} disabled={isPending}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить кнопку
        </Button>
      </div>

      {buttons.length === 0 && (
        <p className="text-sm text-muted-foreground">Кнопок пока нет</p>
      )}

      {buttons.map((button, index) => (
        <div key={`button-${index}`} className="grid gap-3 rounded-lg border p-3 md:grid-cols-[1fr_1fr_auto]">
          <Input value={button.text} onChange={(e) => onUpdate(index, { text: e.target.value })} placeholder="Текст кнопки" disabled={isPending} />
          <Input value={button.url ?? ''} onChange={(e) => onUpdate(index, { url: e.target.value })} placeholder="https://..." disabled={isPending} />
          <Button type="button" variant="ghost" size="icon" onClick={() => onRemove(index)} disabled={isPending}>
            <Trash2 className="h-4 w-4" />
          </Button>
          {showCallbackData && (
            <Input
              value={button.callbackData ?? ''}
              onChange={(e) => onUpdate(index, { callbackData: e.target.value })}
              placeholder="Данные обратного вызова (техническое поле)"
              className="md:col-span-2"
              disabled={isPending}
            />
          )}
        </div>
      ))}
    </div>
  );
});
